import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Section, Eyebrow, Heading } from "../Chapter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const LetterForm = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
    const [status, setStatus] = useState("idle"); // idle | sending | sealed
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast("Who should I say it's from?");
        if (!EMAIL_RE.test(form.email.trim())) return toast("That address won't find its way back to you.");
        if (!form.message.trim()) return toast("The page is still blank.");

        setStatus("sending");
        try {
            await axios.post(`${API}/letters`, form);
            setStatus("sealed");
        } catch (err) {
            setStatus("idle");
            if (err?.response?.status === 429) {
                toast("You've sent a few already — give it a little while.");
            } else {
                toast("The letter didn't make it. Try again, or write directly to vinay@patr.ai.");
            }
        }
    };

    return (
        <Section id="write">
            <Eyebrow>XI. Your turn</Eyebrow>
            <Heading>Write a letter</Heading>

            <div className="mx-auto max-w-[46rem]">
                <div className="relative rounded-sm border border-rule bg-paper shadow-[0_1px_0_var(--rule),0_40px_80px_-50px_rgba(31,27,22,0.55)]">
                    {/* red margin rule, like a notebook */}
                    <span className="pointer-events-none absolute left-10 top-0 hidden h-full w-px bg-wax/20 md:block" />

                    <AnimatePresence mode="wait">
                        {status !== "sealed" ? (
                            <motion.form
                                key="paper"
                                onSubmit={submit}
                                exit={{ scaleY: 0.12, opacity: 0 }}
                                style={{ transformOrigin: "top" }}
                                transition={{ duration: 0.5, ease: [0.6, 0, 0.4, 1] }}
                                className="space-y-9 p-8 md:p-14 md:pl-20"
                            >
                                <div className="font-deva text-3xl text-ink/70">पत्र</div>

                                <label className="block">
                                    <span className="font-sans text-[0.7rem] tracking-[0.28em] uppercase text-sepia">From</span>
                                    <input
                                        data-testid="letter-form-sender"
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your name"
                                        className="underline-field mt-3 w-full py-2 font-serif text-xl text-ink placeholder:text-sepia/50"
                                    />
                                </label>

                                <label className="block">
                                    <span className="font-sans text-[0.7rem] tracking-[0.28em] uppercase text-sepia">Where to write back</span>
                                    <input
                                        data-testid="letter-form-email"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="your@email.com"
                                        className="underline-field mt-3 w-full py-2 font-serif text-xl text-ink placeholder:text-sepia/50"
                                    />
                                </label>

                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={form.website}
                                    onChange={set("website")}
                                    aria-hidden="true"
                                    className="absolute left-[-9999px] h-0 w-0 opacity-0"
                                />

                                <label className="block">
                                    <span className="font-sans text-[0.7rem] tracking-[0.28em] uppercase text-sepia">The letter</span>
                                    <textarea
                                        data-testid="letter-form-content"
                                        value={form.message}
                                        onChange={set("message")}
                                        rows={10}
                                        placeholder="Take your time."
                                        className="ruled mt-4 w-full resize-none bg-transparent font-serif text-xl text-ink outline-none placeholder:text-sepia/50"
                                    />
                                </label>

                                <button
                                    data-testid="letter-form-submit"
                                    type="submit"
                                    disabled={status === "sending"}
                                    className="group inline-flex items-center gap-3 rounded-full bg-wax px-9 py-3.5 font-sans text-sm tracking-[0.15em] uppercase text-paper transition-[transform,opacity] duration-300 hover:-translate-y-0.5 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-wax focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                                >
                                    {status === "sending" ? "Sealing…" : "Send it"}
                                </button>
                            </motion.form>
                        ) : (
                            <Sealed key="sealed" />
                        )}
                    </AnimatePresence>
                </div>

                <p className="mt-10 text-center font-serif text-lg text-sepia">
                    <a
                        data-testid="mailto-link"
                        href="mailto:vinay@patr.ai"
                        className="text-wax underline decoration-wax/40 underline-offset-4 transition-colors hover:decoration-wax"
                    >
                        vinay@patr.ai
                    </a>
                </p>
            </div>
        </Section>
    );
};

const Sealed = () => (
    <motion.div
        data-testid="letter-sent-confirmation"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="flex min-h-[420px] flex-col items-center justify-center px-8 py-16 text-center"
    >
        <motion.div
            initial={{ scale: 3, opacity: 0, rotate: -16 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ duration: 0.55, ease: [0.34, 1.3, 0.64, 1], delay: 0.1 }}
            className="relative flex h-24 w-24 items-center justify-center rounded-full bg-wax shadow-[0_16px_40px_-16px_rgba(139,44,44,0.85)]"
        >
            <span className="absolute inset-[6px] rounded-full border border-paper/30" />
            <span className="font-deva text-3xl text-paper">प</span>
        </motion.div>

        <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mx-auto mt-10 max-w-[34ch] font-serif text-2xl leading-snug text-ink md:text-3xl"
        >
            It's on its way. No runner, no steamer, no month of waiting. Vinay will read it —
            probably today.
        </motion.p>
    </motion.div>
);
